/**
 * Grade context — reactive study year state.
 *
 * `years`         list of active StudyYear objects (id, name, order)
 * `loaded`        list has been fetched
 * `loadYears()`   idempotent fetch
 * `findByOrder(n)`     lookup helper
 * `resolveYearParam(v)` normalize URL param → order int or null
 */

import { list_public_study_years } from '$lib/api-calls.js'
import { preferences } from '$lib/stores/preferences.svelte.js'
import { session } from '../../store.svelte.js'

export const gradeState = $state({
  years: [],
  loaded: false,
})

let inflight = null

export function loadYears() {
  if (gradeState.loaded || inflight) return inflight ?? Promise.resolve()
  inflight = list_public_study_years({ withContent: true })
    .then((r) => {
      gradeState.years = (r.response_data || []).slice().sort((a, b) => a.order - b.order)
      gradeState.loaded = true
    })
    .catch(() => {
      gradeState.loaded = true
    })
    .finally(() => { inflight = null })
  return inflight
}

export function findByOrder(order) {
  const n = Number(order)
  if (!Number.isFinite(n)) return null
  return gradeState.years.find((y) => y.order === n) ?? null
}

export function resolveYearParam(raw) {
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Resolve preferred study year for UI fallbacks (nav bases, redirects).
 *
 * Priority:
 *   1. `preferences.currentYear` (localStorage) — single source of truth
 *   2. `session.user.study_year.order` — only when localStorage empty/unknown
 *   3. First populated year from `gradeState.years`
 *   4. Hard fallback: 1
 *
 * When `gradeState.years` is empty (not yet loaded), skip list validation so
 * callers still get a usable value instead of always falling to profile.
 */
export function preferredYear() {
  const years = gradeState.years
  const inList = (n) => !years.length || years.some((y) => y.order === n)
  const remembered = preferences.currentYear
  if (remembered && inList(remembered)) return remembered
  const profileOrder = session.user?.study_year?.order
  if (profileOrder && inList(profileOrder)) return profileOrder
  return years[0]?.order ?? profileOrder ?? 1
}
